import { auth } from '../config/firebase';
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import { API_CONFIG } from '../config/api';
import { logInfo, logError, setLogUserContext, clearLogUserContext } from '../services/logtail';
import ga from '../services/analytics';
import { getStoredUtm, removeParamsPreservingUtm, preserveUtmParams, mergePathQuery } from '../utils/utm';
import { getMetaAttribution } from '../utils/metaAttribution';
import { relayMetaCapiEvent } from '../services/metaCapiRelay';
import { completeCloudChoice as completeCloudChoiceApi } from '../services/anonymousAuthService';
import type { StorageProvider, MigrationStatus } from '../services/storageService';
import {
  consumeStorageReturnPath,
  writeMigrationWizardState,
  STORAGE_CALLBACK_HANDLING_KEY,
  storageSettingsSectionPath,
  clearStorageOAuthPending,
} from '../services/storageService';

/**
 * authStore - the single source of truth for Firebase auth + backend profile sync.
 *
 * This module owns the ONLY `onAuthStateChanged` listener and the `/users/me`
 * sync. React reads it through `useSyncExternalStore` (see AuthContext), and
 * route loaders await `whenReady()` before deciding any redirect, so navigation
 * decisions never race a half-loaded auth state.
 */

export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

export interface AuthSnapshot {
  status: AuthStatus;
  user: FirebaseUser | null;
  idToken: string | null;
  userType: 'starter' | 'artist' | 'studio';
  includesCassetteCloud: boolean;
  /** True when user may use Cassette Cloud as main project storage (not platform availability). */
  eligibleForCassetteProjectStorage: boolean;
  promoCode?: string;
  promoExpiry?: Date;
  driveLinked: boolean;
  driveEmail: string | null;
  storageProvider: StorageProvider | '';
  usingCassetteCloud: boolean;
  migrationStatus: MigrationStatus;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  isNewUser: boolean;
  isAnonymous: boolean;
  needsCloudChoice: boolean;
  /** From backend DEFAULT_TRACK_ID (start-backend.sh) via GET /users/me */
  defaultTrackId: string | null;
  /** False when Cassette Cloud is selected but R2/S3 is not configured on the server. */
  s3Ready: boolean;
}

// Watchdog must align with the service worker NetworkFirst navigation timeout
// (src/sw.ts networkTimeoutSeconds: 8) so a flaky/offline launch never spins the
// boot loader forever.
const READY_WATCHDOG_MS = 8000;
const SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000;

const initialSnapshot: AuthSnapshot = {
  status: 'initializing',
  user: null,
  idToken: null,
  userType: 'starter',
  includesCassetteCloud: false,
  eligibleForCassetteProjectStorage: true,
  promoCode: undefined,
  promoExpiry: undefined,
  driveLinked: false,
  driveEmail: null,
  storageProvider: '',
  usingCassetteCloud: false,
  migrationStatus: 'idle',
  firstName: undefined,
  lastName: undefined,
  displayName: undefined,
  isNewUser: false,
  isAnonymous: false,
  needsCloudChoice: false,
  defaultTrackId: null,
  s3Ready: true,
};

let snapshot: AuthSnapshot = initialSnapshot;
const listeners = new Set<() => void>();

let isReady = false;
let readyResolve!: (s: AuthSnapshot) => void;
const readyPromise = new Promise<AuthSnapshot>((resolve) => {
  readyResolve = resolve;
});
let watchdog: ReturnType<typeof setTimeout> | null = null;
let settleWaiters: Array<(s: AuthSnapshot) => void> = [];

let sessionTimeout: ReturnType<typeof setTimeout> | null = null;

// Analytics bookkeeping that used to live in AuthContext refs.
let lastLoginMethod: string | null = null;
let loginLogged = false;

function emit() {
  for (const listener of listeners) listener();
}

function patch(partial: Partial<AuthSnapshot>) {
  snapshot = { ...snapshot, ...partial };
  emit();
}

/** Firebase user.isAnonymous wins when a user is loaded; otherwise use backend snapshot. */
export function resolveIsAnonymous(snap: AuthSnapshot): boolean {
  if (snap.user) return snap.user.isAnonymous;
  return snap.isAnonymous;
}

function markSettled(status: 'authenticated' | 'unauthenticated', partial: Partial<AuthSnapshot> = {}) {
  snapshot = { ...snapshot, ...partial, status };
  emit();
  if (watchdog) {
    clearTimeout(watchdog);
    watchdog = null;
  }
  if (!isReady) {
    isReady = true;
    readyResolve(snapshot);
  }
  const waiters = settleWaiters;
  settleWaiters = [];
  for (const resolve of waiters) resolve(snapshot);
}

// ---- Backend profile sync -------------------------------------------------

async function syncStorageStatusFromBackend(token: string) {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/storage/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      return;
    }
    const data = await response.json();
    const status = data.status as {
      storageLinked?: boolean;
      defaultStorageProvider?: StorageProvider;
      storageProvider?: StorageProvider;
      migrationStatus?: MigrationStatus;
      s3Ready?: boolean;
      accountEmail?: string;
      storageQuotaBytes?: number;
    } | undefined;
    if (!status) {
      return;
    }

    const provider = (status.defaultStorageProvider || status.storageProvider || '') as StorageProvider | '';
    const storageLinked = status.storageLinked === true;
    const s3Ready = status.s3Ready !== false;
    const usingCC = provider === 'cassette_s3' && storageLinked && s3Ready;

    const storagePatch: Partial<AuthSnapshot> = {
      s3Ready,
      driveLinked: storageLinked && (provider !== 'cassette_s3' || s3Ready),
      storageProvider: provider,
      usingCassetteCloud: usingCC,
      migrationStatus: (status.migrationStatus as MigrationStatus) || snapshot.migrationStatus,
    };

    if (provider === 'cassette_s3') {
      storagePatch.driveEmail = null;
    } else if (status.accountEmail) {
      storagePatch.driveEmail = status.accountEmail;
    }

    if (storagePatch.driveLinked) {
      localStorage.setItem('driveLinked', 'true');
      if (provider) {
        localStorage.setItem('storageProvider', provider);
      }
    } else {
      localStorage.removeItem('driveLinked');
      if (provider) {
        localStorage.setItem('storageProvider', provider);
      }
    }

    patch(storagePatch);
  } catch (error) {
    logError('Error syncing storage status', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

async function createNewUser(token: string, firebaseUid: string) {
  try {
    const utm = getStoredUtm();
    const meta = getMetaAttribution();
    const eventId = `signup_${firebaseUid}`;
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH_USER}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...(utm
          ? {
              utmSource: utm.utm_source,
              utmMedium: utm.utm_medium,
              utmCampaign: utm.utm_campaign,
              utmTerm: utm.utm_term,
              utmContent: utm.utm_content,
              firstSeenAt: utm.firstSeenAt,
              landingPage: utm.landingPath,
              initialDesign: utm.initialDesign,
            }
          : {}),
        fbp: meta.fbp,
        fbc: meta.fbc,
        fbclid: meta.fbclid,
        eventId,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.user) {
        logInfo('New user created successfully', { uid: firebaseUid, isNewUser: data.isNewUser });
        if (data.isNewUser) {
          patch({ isNewUser: true });
          if (!data.user.isAnonymous) {
            ga.event('signup_complete', { event_id: eventId });
          }
        }
        patch({
          isAnonymous: !!data.user.isAnonymous,
          needsCloudChoice: !!data.user.needsCloudChoice,
          userType: 'starter',
          promoCode: undefined,
          promoExpiry: undefined,
        });
        await fetchUserInfo(token, firebaseUid);
      } else {
        logError('Failed to create user', { error: data.errorMessage || 'Unknown error', uid: firebaseUid });
      }
    } else {
      logError('Failed to create user', { status: response.status, uid: firebaseUid });
    }
  } catch (error) {
    logError('Error creating new user', {
      error: error instanceof Error ? error.message : String(error),
      uid: firebaseUid,
    });
  }
}

export async function fetchUserInfo(token: string, firebaseUid: string) {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS_ME}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.user) {
        const userData = data.user;

        let currentTier: 'starter' | 'artist' | 'studio' = 'starter';
        let nextPromoCode: string | undefined;
        let nextPromoExpiry: Date | undefined;

        if (userData.subscriptionTier) {
          if (userData.tierUntil) {
            const tierUntilDate = new Date(userData.tierUntil);
            if (tierUntilDate > new Date()) {
              currentTier = userData.subscriptionTier as 'artist' | 'studio';
              ga.setUserProperties({ subscription_tier: currentTier });
              if (userData.activePromoCode) {
                nextPromoCode = userData.activePromoCode;
                nextPromoExpiry = tierUntilDate;
              } else {
                if (userData.premiumUntil) {
                  nextPromoExpiry = new Date(userData.premiumUntil);
                }
                nextPromoCode = undefined;
              }
            } else {
              currentTier = 'starter';
              ga.setUserProperties({ subscription_tier: 'starter' });
            }
          } else if (userData.premiumUntil) {
            const premiumUntilDate = new Date(userData.premiumUntil);
            if (premiumUntilDate > new Date()) {
              currentTier = userData.subscriptionTier as 'artist' | 'studio';
              ga.setUserProperties({ subscription_tier: currentTier });
              if (userData.activePromoCode) {
                nextPromoCode = userData.activePromoCode;
              }
              nextPromoExpiry = premiumUntilDate;
            } else {
              currentTier = 'starter';
              ga.setUserProperties({ subscription_tier: 'starter' });
            }
          } else {
            currentTier = 'starter';
            ga.setUserProperties({ subscription_tier: 'starter' });
          }
        } else {
          currentTier = 'starter';
          ga.setUserProperties({ subscription_tier: 'starter' });
        }

        let nextDisplayName: string;
        if (userData.firstName && userData.lastName) {
          nextDisplayName = `${userData.firstName} ${userData.lastName}`;
        } else if (userData.firstName) {
          nextDisplayName = userData.firstName;
        } else if (userData.email) {
          nextDisplayName = userData.email.split('@')[0];
        } else {
          nextDisplayName = 'User';
        }

        const includesCassetteCloud = !!userData.includesCassetteCloud;
        const eligibleForCassetteProjectStorage =
          !!userData.isAnonymous ||
          currentTier === 'starter' ||
          includesCassetteCloud;

        const baseProfile: Partial<AuthSnapshot> = {
          userType: currentTier,
          includesCassetteCloud,
          eligibleForCassetteProjectStorage,
          promoCode: nextPromoCode,
          promoExpiry: nextPromoExpiry,
          firstName: userData.firstName || undefined,
          lastName: userData.lastName || undefined,
          displayName: nextDisplayName,
          isAnonymous: !!userData.isAnonymous,
          needsCloudChoice: !!userData.needsCloudChoice,
          defaultTrackId: typeof userData.defaultTrackId === 'string' ? userData.defaultTrackId : null,
        };

        if (userData.isAnonymous) {
          patch({
            ...baseProfile,
            usingCassetteCloud: false,
            storageProvider: '',
            driveLinked: false,
            driveEmail: null,
            s3Ready: true,
          });
          await syncStorageStatusFromBackend(token);
        } else {
          const provider = (userData.storageProvider || (userData.gdriveEmail ? 'gdrive' : '')) as StorageProvider | '';
          const backendLinked = userData.storageLinked === true;
          const storagePatch: Partial<AuthSnapshot> = {};

          if (provider === 'cassette_s3' || backendLinked) {
            storagePatch.usingCassetteCloud = provider === 'cassette_s3';
            storagePatch.storageProvider = provider || 'cassette_s3';
            storagePatch.driveLinked = true;
            if (provider === 'cassette_s3') {
              storagePatch.driveEmail = null;
            } else {
              const email = userData.cloudConnection?.accountEmail || userData.gdriveEmail || null;
              storagePatch.driveEmail = email;
              if (email) {
                localStorage.setItem('driveLinked', 'true');
                localStorage.setItem('driveEmail', email);
              }
            }
            localStorage.setItem('storageProvider', provider || 'cassette_s3');
          } else if (provider === 'none') {
            storagePatch.usingCassetteCloud = false;
            storagePatch.storageProvider = 'none';
            storagePatch.driveLinked = false;
            storagePatch.driveEmail = null;
          } else if (userData.gdriveEmail || userData.cloudConnection?.rootFolderId || userData.gdriveRootId) {
            storagePatch.usingCassetteCloud = false;
            storagePatch.storageProvider = provider || 'gdrive';
            storagePatch.driveLinked = true;
            const email = userData.cloudConnection?.accountEmail || userData.gdriveEmail || null;
            storagePatch.driveEmail = email;
            if (email) {
              localStorage.setItem('driveLinked', 'true');
              localStorage.setItem('driveEmail', email);
            }
          }

          if (userData.migrationStatus) {
            storagePatch.migrationStatus = userData.migrationStatus as MigrationStatus;
          }

          patch({ ...baseProfile, ...storagePatch });
          await syncStorageStatusFromBackend(token);
        }

        logInfo('User info fetched from backend', {
          uid: firebaseUid,
          subscriptionTier: userData.subscriptionTier,
          tierUntil: userData.tierUntil,
          userType: currentTier,
        });
      }
    } else if (response.status === 404) {
      logInfo('User not found, creating new user', { uid: firebaseUid });
      await createNewUser(token, firebaseUid);
    } else {
      logError('Failed to fetch user info', { status: response.status, uid: firebaseUid });
    }
  } catch (error) {
    logError('Error fetching user info', {
      error: error instanceof Error ? error.message : String(error),
      uid: firebaseUid,
    });
  }
}

// ---- Storage OAuth redirect callback (runs once on boot) ------------------

async function processStorageCallback() {
  const params = new URLSearchParams(window.location.search);
  const storageSuccess = params.get('storage_success') ?? params.get('gdrive_success');
  const provider = (params.get('provider') ?? 'gdrive') as StorageProvider;
  const storageError = params.get('storage_error') ?? params.get('gdrive_error');
  const isMigration = params.get('migration') === 'true';

  if (storageSuccess !== 'true' && !storageError) {
    return;
  }

  // Claim handling + return path before any await (prevents StorageCallback races).
  sessionStorage.setItem(STORAGE_CALLBACK_HANDLING_KEY, '1');
  const returnTo = consumeStorageReturnPath(storageSettingsSectionPath());

  if (storageSuccess === 'true') {
    const newUrl = removeParamsPreservingUtm([
      'storage_success',
      'storage_email',
      'storage_error',
      'provider',
      'migration',
      'gdrive_success',
      'gdrive_email',
      'gdrive_error',
    ]);
    window.history.replaceState({}, '', newUrl);
    ga.event('storage_connected', { provider });
    window.dispatchEvent(new CustomEvent('cassette-storage-linked', { detail: { provider } }));

    const snap = await authStore.whenReady();
    if (snap.user) {
      try {
        const token = await snap.user.getIdToken();
        try {
          await completeCloudChoiceApi(token);
          patch({ needsCloudChoice: false });
        } catch {
          // non-blocking
        }
        await fetchUserInfo(token, snap.user.uid);
      } catch {
        // ignore
      }
    }

    if (isMigration) {
      const updated = authStore.getSnapshot();
      writeMigrationWizardState({
        step: 'confirm',
        source: (updated.storageProvider || 'gdrive') as StorageProvider,
        target: provider,
      });
      window.location.assign(preserveUtmParams(storageSettingsSectionPath()));
      return;
    }

    const updated = authStore.getSnapshot();
    let destination = returnTo;
    const returnPathOnly = returnTo.split('#')[0].split('?')[0];
    if (updated.isNewUser && (returnPathOnly === '/auth/storage' || returnPathOnly === '/settings')) {
      destination = '/welcome';
    } else if (updated.isNewUser) {
      destination = returnTo;
    }
    window.location.assign(preserveUtmParams(destination));
    return;
  }

  if (storageError) {
    clearStorageOAuthPending();
    logError('Storage OAuth redirect error', { error: storageError, provider });
    const snap = await authStore.whenReady();
    if (snap.user) {
      try {
        const token = await snap.user.getIdToken();
        await fetchUserInfo(token, snap.user.uid);
      } catch {
        // ignore
      }
    }
    const errParams = new URLSearchParams();
    errParams.set('storage_error', storageError);
    if (provider) errParams.set('provider', provider);
    window.location.assign(preserveUtmParams(mergePathQuery(returnTo, errParams)));
  }
}

// ---- Bootstrap ------------------------------------------------------------

let started = false;

export function startAuthStore() {
  if (started) return;
  started = true;

  const path = window.location.pathname;
  if (path === '/auth/gdrive/callback' || path === '/auth/storage/callback') {
    processStorageCallback().catch((error) => {
      logError('Error processing storage callback', { error });
    });
  }

  watchdog = setTimeout(() => {
    if (!isReady) {
      logError('Auth bootstrap watchdog fired - resolving without backend sync');
      const fbUser = auth.currentUser;
      markSettled(fbUser ? 'authenticated' : 'unauthenticated', fbUser ? { user: fbUser } : {});
    }
  }, READY_WATCHDOG_MS);

  onAuthStateChanged(auth, (firebaseUser) => {
    if (firebaseUser) {
      patch({ status: 'initializing' });
      firebaseUser
        .getIdToken(true)
        .then(async (token) => {
          patch({
            user: firebaseUser,
            idToken: token,
            isAnonymous: firebaseUser.isAnonymous,
          });

          logInfo('ID token retrieved successfully', { uid: firebaseUser.uid });
          setLogUserContext(firebaseUser.uid, firebaseUser.email ?? null);
          ga.setUserId(firebaseUser.uid);
          ga.setUserProperties({ site_url: window.location.host });
          if (!loginLogged) {
            const method = lastLoginMethod || 'session';
            ga.event('login', { method });
            if (method === 'anonymous') {
              const leadEventId = `lead_anon_${firebaseUser.uid}`;
              ga.event('anon_sign_in', { event_id: leadEventId });
              relayMetaCapiEvent('Lead', { eventId: leadEventId });
            }
            loginLogged = true;
            lastLoginMethod = null;
          }

          try {
            await fetchUserInfo(token, firebaseUser.uid);
          } catch (error) {
            logError('Failed to sync user profile after auth', {
              error: error instanceof Error ? error.message : String(error),
              uid: firebaseUser.uid,
            });
          } finally {
            markSettled('authenticated');
          }

          if (sessionTimeout) clearTimeout(sessionTimeout);
          sessionTimeout = setTimeout(() => {
            logInfo('Session timeout - logging out user', { uid: firebaseUser.uid });
            void forceSignOut();
          }, SESSION_TIMEOUT_MS);
        })
        .catch((error) => {
          logError('Failed to get ID token', { error, uid: firebaseUser.uid });
          setLogUserContext(firebaseUser.uid, firebaseUser.email ?? null);
          markSettled('authenticated', { user: firebaseUser, idToken: null });
        });

      // Optimistic cached storage state for signed-in permanent users (backend sync is authoritative).
      if (!firebaseUser.isAnonymous) {
        const cachedDriveLinked = localStorage.getItem('driveLinked') === 'true';
        const cachedDriveEmail = localStorage.getItem('driveEmail');
        if (cachedDriveLinked && cachedDriveEmail) {
          patch({ driveLinked: true, driveEmail: cachedDriveEmail });
        }
      }
    } else {
      if (sessionTimeout) {
        clearTimeout(sessionTimeout);
        sessionTimeout = null;
      }
      clearLogUserContext();
      ga.setUserId('');
      loginLogged = false;
      localStorage.removeItem('driveLinked');
      localStorage.removeItem('driveEmail');
      localStorage.removeItem('storageProvider');
      markSettled('unauthenticated', {
        user: null,
        idToken: null,
        userType: 'starter',
        promoCode: undefined,
        promoExpiry: undefined,
        driveLinked: false,
        driveEmail: null,
        firstName: undefined,
        lastName: undefined,
        displayName: undefined,
        isNewUser: false,
        isAnonymous: false,
        needsCloudChoice: false,
        defaultTrackId: null,
        s3Ready: true,
      });
    }
  });
}

async function forceSignOut() {
  try {
    localStorage.removeItem('driveLinked');
    localStorage.removeItem('driveEmail');
    await firebaseSignOut(auth);
  } catch (error) {
    logError('Error during forced sign out', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

// ---- Public API -----------------------------------------------------------

/** Re-fetch /storage/status and patch auth snapshot (e.g. after migration ends). */
export async function refreshMigrationAuthStatus(): Promise<void> {
  const token = snapshot.idToken;
  if (!token) {
    return;
  }
  await syncStorageStatusFromBackend(token);
}

export const authStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot(): AuthSnapshot {
    return snapshot;
  },
  /**
   * Waits for the first auth bootstrap, then always returns the current snapshot.
   * Loaders must see post-logout state, not the stale first-settle snapshot.
   */
  whenReady(): Promise<AuthSnapshot> {
    if (isReady) {
      return Promise.resolve(snapshot);
    }
    return readyPromise;
  },
  /** Resolves on the next settle event - used after an interactive sign-in. */
  waitForSettled(): Promise<AuthSnapshot> {
    return new Promise((resolve) => settleWaiters.push(resolve));
  },
  patch,
  fetchUserInfo,
  setLastLoginMethod(method: string | null) {
    lastLoginMethod = method;
  },
  resetLoginLogged() {
    loginLogged = false;
  },
};
